import React, { useState } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { supabase } from '../../lib/supabaseClient';
import {
  CSV_SCHEMAS,
  downloadCSVTemplate,
  parseCSV,
  validateCSVData,
} from '../../utils/csvTemplates';

const CSVImport = () => {
  const [entityType, setEntityType] = useState('rfqs');
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');

  const handleDownloadTemplate = () => {
    try {
      downloadCSVTemplate(entityType);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template: ' + error.message);
    }
  };

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setImportResults(null);

    try {
      // Read file content
      const text = await selectedFile.text();

      // Parse CSV
      const data = parseCSV(text);
      setParsedData(data);

      // Validate data
      const validation = validateCSVData(data, entityType);
      setValidationErrors(validation.errors);
    } catch (error) {
      console.error('Error parsing CSV:', error);
      alert('Error parsing CSV file: ' + error.message);
      setFile(null);
      setParsedData([]);
      setValidationErrors([]);
    }
  };

  const handleImport = async () => {
    if (validationErrors.length > 0) {
      alert('Please fix validation errors before importing');
      return;
    }

    setImporting(true);
    setImportResults(null);
    setUploadProgress('');

    try {
      // For RFQs, handle client creation first if needed
      if (entityType === 'rfqs') {
        // Get all existing clients
        const { data: existingClients, error: clientsFetchError } = await supabase
          .from('clients')
          .select('id, name');

        if (clientsFetchError) throw clientsFetchError;

        const clientMap = new Map(existingClients.map((c) => [c.name.toLowerCase(), c.id]));
        const clientsToCreate = new Set();

        // Check which clients need to be created
        parsedData.forEach((row) => {
          const clientId = row.client_id;
          if (clientId && !clientMap.has(clientId.toLowerCase())) {
            clientsToCreate.add(clientId);
          }
        });

        // Create missing clients with Pending status
        if (clientsToCreate.size > 0) {
          const newClients = Array.from(clientsToCreate).map((name) => ({
            name,
            status: 'Pending',
          }));

          const { data: createdClients, error: createError } = await supabase
            .from('clients')
            .insert(newClients)
            .select('id, name');

          if (createError) throw createError;

          // Update client map with newly created clients
          createdClients.forEach((client) => {
            clientMap.set(client.name.toLowerCase(), client.id);
          });

          console.log(`Auto-created ${createdClients.length} clients with Pending status`);
        }

        // Now replace client names with client IDs in parsed data
        parsedData.forEach((row) => {
          if (row.client_id) {
            const clientId = clientMap.get(row.client_id.toLowerCase());
            if (clientId) {
              row.client_id = clientId;
            }
          }
        });
      }

      // Transform data for Supabase
      const transformedData = parsedData.map((row) => {
        const transformed = { ...row };

        // Entity-specific transformations
        if (entityType === 'rfqs') {
          // Convert locations string to array if needed
          if (typeof transformed.locations === 'string' && transformed.locations) {
            transformed.locations = transformed.locations.split(',').map((loc) => loc.trim());
          }
          // Convert initial_term_months to integer
          if (transformed.initial_term_months) {
            transformed.initial_term_months = parseInt(transformed.initial_term_months);
          }
          // Handle empty strings for nullable fields
          Object.keys(transformed).forEach((key) => {
            if (transformed[key] === '') {
              transformed[key] = null;
            }
          });
        } else if (entityType === 'clients') {
          // Convert boolean strings
          if (transformed.is_rcti_client) {
            transformed.is_rcti_client = transformed.is_rcti_client.toLowerCase() === 'true';
          }
          // Handle empty strings
          Object.keys(transformed).forEach((key) => {
            if (transformed[key] === '') {
              transformed[key] = null;
            }
          });
        } else if (entityType === 'candidates') {
          // Convert boolean strings
          const booleanFields = ['open_to_new_roles', 'open_to_client_contact'];
          booleanFields.forEach((field) => {
            if (transformed[field]) {
              transformed[field] = transformed[field].toLowerCase() === 'true';
            }
          });

          // Store skills separately for backend processing (don't include in candidate insert)
          if (typeof transformed.skills === 'string' && transformed.skills) {
            transformed._skills = transformed.skills.split(/[,;]/).map((skill) => skill.trim()).filter(Boolean);
          }
          delete transformed.skills; // Remove from candidate insert

          // Handle empty strings
          Object.keys(transformed).forEach((key) => {
            if (transformed[key] === '' && key !== '_skills') {
              transformed[key] = null;
            }
          });
        }

        return transformed;
      });

      // For candidates, use backend API to handle skills normalization
      if (entityType === 'candidates') {
        const API_URL = process.env.REACT_APP_API_URL;
        const { data: { session } } = await supabase.auth.getSession();

        // Split into batches of 50 to avoid serverless timeout
        const BATCH_SIZE = 50;
        const batches = [];
        for (let i = 0; i < transformedData.length; i += BATCH_SIZE) {
          batches.push(transformedData.slice(i, i + BATCH_SIZE));
        }

        console.log(`[CSV Import] Processing ${transformedData.length} candidates in ${batches.length} batches of ${BATCH_SIZE}`);

        // Process batches sequentially
        let totalImported = 0;
        let totalSkillsCreated = 0;
        let totalSkillsMapped = 0;
        let totalFailed = 0;
        const allDetails = [];

        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          const batch = batches[batchIndex];
          const batchNum = batchIndex + 1;

          console.log(`[CSV Import] Processing batch ${batchNum}/${batches.length} (${batch.length} candidates)`);
          setUploadProgress(`Processing batch ${batchNum}/${batches.length}...`);

          try {
            const response = await fetch(`${API_URL}/admin/candidates/csv-import`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ candidates: batch })
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
              throw new Error(result.error || result.message || `Batch ${batchNum} failed`);
            }

            // Accumulate results
            totalImported += result.results.imported || 0;
            totalSkillsCreated += result.results.skillsCreated || 0;
            totalSkillsMapped += result.results.skillsMapped || 0;
            totalFailed += result.results.failed || 0;
            if (result.results.details) {
              allDetails.push(...result.results.details);
            }

            console.log(`[CSV Import] Batch ${batchNum} complete: ${result.results.imported} imported, ${result.results.failed} failed`);
          } catch (error) {
            console.error(`[CSV Import] Batch ${batchNum} error:`, error);
            // Continue with next batch even if one fails
            totalFailed += batch.length;
          }
        }

        setImportResults({
          success: true,
          count: totalImported,
          message: `Successfully imported ${totalImported} candidates in ${batches.length} batches. ` +
                   `Created ${totalSkillsCreated} pending skills, ` +
                   `mapped ${totalSkillsMapped} total skills. ` +
                   (totalFailed > 0 ? `${totalFailed} failed.` : ''),
          details: allDetails
        });
      } else if (entityType === 'skills') {
        // For skills, use backend API to handle two-phase processing (non-mapping first, then mapping)
        const API_URL = process.env.REACT_APP_API_URL;
        const { data: { session } } = await supabase.auth.getSession();

        const response = await fetch(`${API_URL}/admin/skills/csv-import`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ skills: transformedData })
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || result.message || 'Import failed');
        }

        setImportResults({
          success: true,
          count: result.results.imported,
          message: `Successfully imported ${result.results.imported} skills. ` +
                   `Created ${result.results.activeCreated} active, ` +
                   `${result.results.pendingCreated} pending, ` +
                   `${result.results.rejectedCreated} rejected, ` +
                   `${result.results.mappingCreated} mapping skills. ` +
                   (result.results.failed > 0 ? `${result.results.failed} failed.` : ''),
          details: result.results.details
        });
      } else {
        // For other entity types, use direct Supabase insert
        const { data, error } = await supabase.from(entityType).insert(transformedData).select();

        if (error) throw error;

        setImportResults({
          success: true,
          count: data.length,
          message: `Successfully imported ${data.length} ${entityType}`,
        });
      }

      // Reset form
      setFile(null);
      setParsedData([]);
      setValidationErrors([]);

      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error importing data:', error);
      setImportResults({
        success: false,
        message: `Error importing data: ${error.message}`,
      });
    } finally {
      setImporting(false);
      setUploadProgress('');
    }
  };

  const schema = CSV_SCHEMAS[entityType];

  return (
    <div className="space-y-6">
      {/* Entity Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle>CSV Import</CardTitle>
          <CardDescription>
            Import {entityType} data from a CSV file. Download a template to get started.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Select Entity Type</label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rfqs">RFQs</SelectItem>
                  <SelectItem value="clients">Clients</SelectItem>
                  <SelectItem value="candidates">Candidates</SelectItem>
                  <SelectItem value="skills">Skills</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleDownloadTemplate} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>

          {/* Template Info */}
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-3">
                <div>
                  <p className="font-medium mb-2">Required Fields:</p>
                  <div className="flex flex-wrap gap-2">
                    {schema.requiredFields.map((field) => (
                      <Badge key={field} variant="destructive" className="font-mono text-xs">
                        {field} *
                      </Badge>
                    ))}
                  </div>
                </div>
                {schema.optionalFields && (
                  <div>
                    <p className="font-medium mb-2">Available Optional Fields:</p>
                    <div className="flex flex-wrap gap-2">
                      {schema.optionalFields.map((field) => (
                        <Badge key={field} variant="secondary" className="font-mono text-xs">
                          {field}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>

          {/* File Upload */}
          <div>
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div className="flex items-center gap-4">
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-primary file:text-primary-foreground
                  hover:file:bg-primary/90
                  cursor-pointer"
              />
              {file && (
                <Badge variant="outline">
                  <FileText className="h-3 w-3 mr-1" />
                  {file.name}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-2">Validation Errors:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Import Results */}
      {importResults && (
        <Alert variant={importResults.success ? 'default' : 'destructive'}>
          {importResults.success ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium">{importResults.message}</p>

              {/* Show failed imports if any */}
              {importResults.details?.failed && importResults.details.failed.length > 0 && (
                <div className="mt-4 p-3 bg-destructive/10 rounded-md border border-destructive/20">
                  <p className="font-semibold text-sm mb-2">Failed Imports ({importResults.details.failed.length}):</p>
                  <div className="max-h-60 overflow-y-auto">
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      {importResults.details.failed.map((failure, idx) => (
                        <li key={idx}>
                          Row {failure.row}: {failure.name} - {failure.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Data Preview */}
      {parsedData.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Data Preview</CardTitle>
                <CardDescription>
                  {parsedData.length} row{parsedData.length !== 1 ? 's' : ''} found
                </CardDescription>
              </div>
              <Button
                onClick={handleImport}
                disabled={validationErrors.length > 0 || importing}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {uploadProgress || 'Importing...'}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import Data
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {schema.headers.map((header) => (
                      <TableHead key={header} className="min-w-[150px]">
                        {header}
                        {schema.requiredFields.includes(header) && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.slice(0, 10).map((row, index) => (
                    <TableRow key={index}>
                      {schema.headers.map((header) => (
                        <TableCell key={header} className="font-mono text-xs">
                          {row[header] || '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {parsedData.length > 10 && (
                <p className="text-sm text-muted-foreground mt-4 text-center">
                  Showing first 10 of {parsedData.length} rows
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CSVImport;
